import React, { useEffect, useState } from 'react'
import { Modal, Select, InputNumber, Input } from 'antd'
import styled from 'styled-components'
import { useDispatch, useSelector } from 'react-redux'
import { editIngredient } from 'src/features/RecipeBuilderSlice'
import {
  editProduct,
  fetchVenueProducts,
} from 'src/features/VenueProductsSlice'
import { normalizeAllergenKey } from 'src/services/allergenUpdateService'
import allergenEventEmitter from 'src/utils/allergenEventEmitter'

const { Option, OptGroup } = Select

const commonAllergens = [
  'Celery',
  'Gluten',
  'Crustaceans',
  'Eggs',
  'Fish',
  'Lupin',
  'Milk',
  'Molluscs',
  'Mustard',
  'Nuts',
  'Peanuts',
  'Sesame seeds',
  'Soya',
  'Sulphites',
]

const ConversionBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  .ant-input {
    width: inherit;
  }

  p {
    margin-top: 10px;
  }
`

const ConversionModal = ({
  showEditIngredient,
  setShowEditIngredient,
  ingredient,
  formState,
  setFormState,
  index,
  onAllergenUpdate,
}) => {
  const dispatch = useDispatch()
  const venueId = useSelector(state => state.venue?.venue?._id)
  const { products: venueProducts } = useSelector(state => state.venueProducts)
  const [loading, setLoading] = useState(false)
  const [allergens, setAllergens] = useState({})
  const [title, setTitle] = useState('')
  const [canEditIngredient, setCanEditIngredient] = useState(true)
  const [conversion, setConversion] = useState({
    label: '',
    product: '',
    size: '',
    toQty: 0,
    toUnit: '',
  })

  useEffect(() => {
    if (ingredient && Object.keys(ingredient).length > 0) {
      // Reset canEditIngredient flag
      setCanEditIngredient(true)

      // Ensure we have a valid product ID
      const productId =
        ingredient.product?.venueProductId ||
        ingredient.venueProductId ||
        ingredient.productId ||
        ingredient._id

      if (!productId) {
        console.error('No valid product ID found for ingredient:', ingredient)
        setCanEditIngredient(false)
        return
      }

      setConversion({
        product: productId,
        size: ingredient.defaultSize || ingredient.size,
      })
      setTitle(ingredient.title || '')

      // Always fetch fresh allergen data from Product Allergens source (venueProducts)
      const fetchFreshAllergenData = async () => {
        try {
          console.log(
            'EditIngredientModal: Fetching fresh venue products data...',
          )
          await dispatch(fetchVenueProducts(venueId))
          console.log(
            'EditIngredientModal: Venue products data fetched successfully',
          )
        } catch (error) {
          console.error(
            'Error fetching venue products for allergen data:',
            error,
          )
        }
      }

      fetchFreshAllergenData()
    }
  }, [ingredient, venueId, dispatch])

  // Separate useEffect to initialize allergens from fresh venueProducts data
  useEffect(() => {
    if (
      ingredient &&
      Object.keys(ingredient).length > 0 &&
      venueProducts &&
      venueProducts.length > 0
    ) {
      const productId =
        ingredient.product?.venueProductId ||
        ingredient.venueProductId ||
        ingredient.productId ||
        ingredient._id

      // Find the current product data from venueProducts (Product Allergens source)
      console.log(
        'EditIngredientModal: Looking for product with ID:',
        productId,
      )
      console.log(
        'EditIngredientModal: Available venueProducts:',
        venueProducts.map(p => ({
          _id: p._id,
          venueProductId: p.venueProductId,
          title: p.title,
          productTitle: p.product?.title,
          allergens: p.allergens,
          allergenTraces: p.allergenTraces,
        })),
      )

      let currentProduct = venueProducts.find(
        p =>
          p._id === productId ||
          p.venueProductId === productId ||
          p.product?._id === productId,
      )

      // If not found by ID, try to find by title as fallback
      if (!currentProduct && ingredient.title) {
        console.log(
          'EditIngredientModal: Product not found by ID, trying to find by title:',
          ingredient.title,
        )
        currentProduct = venueProducts.find(
          p =>
            p.title === ingredient.title ||
            p.product?.title === ingredient.title ||
            p.title?.toLowerCase() === ingredient.title?.toLowerCase(),
        )
        if (currentProduct) {
          console.log(
            'EditIngredientModal: Found product by title:',
            currentProduct,
          )
        }
      }

      // Additional fallback: try to find by product title from ingredient.product.title
      if (!currentProduct && ingredient.product?.title) {
        console.log(
          'EditIngredientModal: Product not found by ingredient title, trying product title:',
          ingredient.product.title,
        )
        currentProduct = venueProducts.find(
          p =>
            p.title === ingredient.product.title ||
            p.product?.title === ingredient.product.title ||
            p.title?.toLowerCase() === ingredient.product.title?.toLowerCase(),
        )
        if (currentProduct) {
          console.log(
            'EditIngredientModal: Found product by product title:',
            currentProduct,
          )
        }
      }

      if (currentProduct) {
        console.log(
          'EditIngredientModal: Using fresh allergen data from Product Allergens source:',
          currentProduct,
        )
        console.log(
          'EditIngredientModal: Product allergens array:',
          currentProduct.allergens,
        )
        console.log(
          'EditIngredientModal: Product allergenTraces array:',
          currentProduct.allergenTraces,
        )

        // This ingredient can be edited
        setCanEditIngredient(true)

        // Initialize allergens from the Product Allergens source
        const initialAllergens = {}
        commonAllergens.forEach(allergen => {
          // Use the normalizeAllergenKey function to get the correct canonical format
          const canonicalAllergen = normalizeAllergenKey(allergen)
          console.log(
            `EditIngredientModal: Mapping ${allergen} to canonical key: ${canonicalAllergen}`,
          )

          if (canonicalAllergen) {
            const hasAllergen =
              currentProduct.allergens?.includes(canonicalAllergen) || false
            const isTraces =
              currentProduct.allergenTraces?.includes(canonicalAllergen) ||
              false

            console.log(
              `EditIngredientModal: ${allergen} (${canonicalAllergen}): hasAllergen=${hasAllergen}, isTraces=${isTraces}`,
            )

            initialAllergens[allergen] = {
              value: hasAllergen ? 'yes' : isTraces ? 'traces' : 'no',
              edited: false,
            }
          } else {
            // Fallback for allergens not in the mapping - try direct lowercase match
            const lowerAllergen = allergen.toLowerCase()
            const hasAllergen =
              currentProduct.allergens?.includes(lowerAllergen) || false
            const isTraces =
              currentProduct.allergenTraces?.includes(lowerAllergen) || false

            console.log(
              `EditIngredientModal: ${allergen} (fallback lowercase): hasAllergen=${hasAllergen}, isTraces=${isTraces}`,
            )

            initialAllergens[allergen] = {
              value: hasAllergen ? 'yes' : isTraces ? 'traces' : 'no',
              edited: false,
            }
          }
        })
        console.log(
          'EditIngredientModal: Final initialAllergens object:',
          initialAllergens,
        )
        setAllergens(initialAllergens)
      } else {
        console.warn(
          'EditIngredientModal: Product not found in venueProducts - this ingredient cannot be edited',
        )
        console.log('EditIngredientModal: Ingredient data:', {
          title: ingredient.title,
          productTitle: ingredient.product?.title,
          allergens: ingredient.allergens,
          allergenTraces: ingredient.allergenTraces,
          productAllergens: ingredient.product?.allergens,
          productAllergenTraces: ingredient.product?.allergenTraces,
        })

        // Set a flag to indicate this ingredient cannot be edited
        setCanEditIngredient(false)

        // Still initialize allergens for display purposes, but they won't be editable
        const initialAllergens = {}
        commonAllergens.forEach(allergen => {
          // Try multiple sources for allergen data
          const hasAllergen =
            (ingredient.allergens?.includes &&
              ingredient.allergens.includes(allergen)) ||
            ingredient.allergens?.[allergen] ||
            (ingredient.product?.allergens?.includes &&
              ingredient.product.allergens.includes(allergen)) ||
            ingredient.product?.allergens?.[allergen] ||
            false

          const isTraces =
            (ingredient.allergenTraces?.includes &&
              ingredient.allergenTraces.includes(allergen)) ||
            ingredient.allergenTraces?.[allergen] ||
            (ingredient.product?.allergenTraces?.includes &&
              ingredient.product.allergenTraces.includes(allergen)) ||
            ingredient.product?.allergenTraces?.[allergen] ||
            false

          initialAllergens[allergen] = {
            value: hasAllergen ? 'yes' : isTraces ? 'traces' : 'no',
            edited: false,
          }
        })
        console.log(
          'EditIngredientModal: Fallback initialAllergens object:',
          initialAllergens,
        )
        setAllergens(initialAllergens)
      }
    }
  }, [ingredient, venueProducts])

  // Force refresh venueProducts when modal opens
  useEffect(() => {
    if (showEditIngredient && venueId) {
      console.log('Modal opened, refreshing venueProducts...')
      const refreshData = async () => {
        try {
          await dispatch(fetchVenueProducts(venueId))
          console.log('Venue products refreshed successfully')
        } catch (error) {
          console.error('Error refreshing venue products:', error)
        }
      }
      refreshData()
    }
  }, [showEditIngredient, venueId, dispatch])

  // Listen for allergen updates from other components
  useEffect(() => {
    const handleAllergenUpdate = async payload => {
      // Local ingredient saves already sync Redux; skip expensive full refetch.
      if (payload?.skipFullRecipeReload) return
      if (payload?.venueId && payload.venueId !== venueId) return

      console.log(
        'EditIngredientModal: Received allergen update event, refreshing venue products...',
      )
      try {
        await dispatch(fetchVenueProducts(venueId))
        console.log(
          'EditIngredientModal: Venue products refreshed after allergen update',
        )
      } catch (error) {
        console.error(
          'EditIngredientModal: Error refreshing venue products after allergen update:',
          error,
        )
      }
    }

    allergenEventEmitter.on('allergen-updated', handleAllergenUpdate)

    return () => {
      allergenEventEmitter.off('allergen-updated', handleAllergenUpdate)
    }
  }, [venueId, dispatch])

  return (
    <Modal
      onOk={async () => {
        setLoading(true)

        try {
          // Check if this ingredient can be edited
          if (!canEditIngredient) {
            console.error(
              'This ingredient cannot be edited - no VenueProduct record found',
            )
            setLoading(false)
            return
          }

          // Validate required fields
          if (!conversion.product) {
            console.error('No product ID available')
            setLoading(false)
            return
          }

          const selectedAllergens = Object.keys(allergens).filter(
            key => allergens[key].value === 'yes',
          )
          const selectedTraces = Object.keys(allergens).filter(
            key => allergens[key].value === 'traces',
          )

          console.log('Sending edit ingredient request:', {
            title,
            conversion,
            allergens: selectedAllergens,
            allergenTraces: selectedTraces,
          })

          // Update the ingredient using the unified allergen service
          console.log(
            'EditIngredientModal: Sending editIngredient request with data:',
            {
              venueId,
              title,
              conversion,
              allergens: selectedAllergens,
              allergenTraces: selectedTraces,
            },
          )

          const updatedProduct = await dispatch(
            editIngredient({
              venueId,
              title,
              conversion,
              allergens: selectedAllergens,
              allergenTraces: selectedTraces,
            }),
          )

          if (updatedProduct) {
            console.log(
              'EditIngredientModal: editIngredient successful, updated product:',
              updatedProduct,
            )

            // Keep shopping-list products in sync without refetching the full products list.
            dispatch(editProduct(updatedProduct))

            const updatedIngredients = [...formState.ingredients]

            const { product, conversions, title } = updatedProduct

            // Emit allergen update event to trigger real-time updates
            console.log('EditIngredientModal: Emitting allergen-updated event')
            allergenEventEmitter.emit('allergen-updated', {
              type: 'ingredient-updated',
              productId: conversion.product,
              title: title,
              allergens: selectedAllergens,
              allergenTraces: selectedTraces,
              venueId,
              skipFullRecipeReload: true,
            })

            updatedIngredients[index] = {
              ...updatedIngredients[index],
              ...product,
              title: title,
              conversions: conversions,
              UoM: updatedIngredients[index].UoM,
              size: updatedIngredients[index].size,
            }

            setFormState({
              ...formState,
              ingredients: updatedIngredients,
            })

            setLoading(false)
            setShowEditIngredient(false)

            // Notify parent locally; avoid triggering a full recipes refetch.
            if (onAllergenUpdate) {
              onAllergenUpdate({ skipFullRecipeReload: true })
            }
          } else {
            console.error('No updated product returned from API')
            setLoading(false)
            // Don't close modal on error
          }
        } catch (error) {
          setLoading(false)
          console.error('Error editing ingredient:', error)
          // Don't close modal on error - let user try again
        }
      }}
      confirmLoading={loading}
      okText={canEditIngredient ? 'Save' : 'Cannot Edit'}
      okButtonProps={{ disabled: !canEditIngredient }}
      width={1200}
      title='Edit Ingredient'
      visible={showEditIngredient}
      onCancel={() => setShowEditIngredient(false)}
    >
      {!canEditIngredient && (
        <div
          style={{
            background: '#fff2e8',
            border: '1px solid #ffb366',
            borderRadius: '4px',
            padding: '12px',
            marginBottom: '16px',
            color: '#d46b08',
          }}
        >
          <strong>⚠️ Ingredient Removed from Shopping List</strong>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
            <strong>{ingredient?.product?.title || ingredient?.title}</strong>{' '}
            has been removed from your venue&apos;s shopping list. You cannot
            edit allergens for this ingredient. To edit allergens, please add it
            back to your shopping list first.
          </p>
        </div>
      )}

      <p>Title</p>
      <Input
        placeholder='title'
        value={title}
        onChange={e => setTitle(e.target.value)}
        disabled={!canEditIngredient}
      />
      {ingredient?.conversions?.length > 0 && (
        <div style={{ marginTop: 20 }}>
          <p>Previous Conversions</p>
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
              marginBottom: 16,
            }}
          >
            {ingredient.conversions.map((conv, i) => (
              <div
                key={i}
                style={{
                  display: 'flex',
                  gap: 8,
                  alignItems: 'center',
                  border: '1px solid #d9d9d9',
                  padding: 8,
                  borderRadius: 4,
                }}
              >
                <strong>{conv.label || 'No Label'}</strong>
                <span>
                  {ingredient.defaultSize} → {conv.toQty} {conv.toUnit}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p style={{ marginTop: 20 }}>New Conversion</p>
      <ConversionBox>
        <p>Label</p>
        <Input
          placeholder='Conversion Label'
          value={conversion.label}
          onChange={e =>
            setConversion({
              ...conversion,
              label: e.target.value,
            })
          }
        />

        <p>From</p>
        <InputNumber disabled value={1} min={0} />
        <Input disabled value={conversion.size} min={0} />
        <p>To</p>
        <InputNumber
          value={conversion.toQty}
          min={0}
          disabled={!canEditIngredient}
          onChange={value =>
            setConversion({
              ...conversion,
              toQty: value,
            })
          }
        />
        <Select
          value={conversion.toUnit}
          placeholder='Unit'
          style={{ width: 150 }}
          disabled={!canEditIngredient}
          onSelect={value =>
            setConversion({
              ...conversion,
              toUnit: value,
            })
          }
        >
          <OptGroup label='Weight'>
            <Option value='kg'>Kilogram (kg)</Option>
            <Option value='g'>Gram (g)</Option>
            <Option value='mg'>Milligram (mg)</Option>
            <Option value='lb'>Pound (lb)</Option>
            <Option value='oz'>Ounce (oz)</Option>
          </OptGroup>
          <OptGroup label='Volume'>
            <Option value='ltr'>Liter (ltr)</Option>
            <Option value='ml'>Milliliter (ml)</Option>
            <Option value='cl'>Centiliter (cl)</Option>
            <Option value='gal'>Gallon (gal)</Option>
            <Option value='floz'>Fluid Ounce (fl oz)</Option>
          </OptGroup>
          <OptGroup label='Count'>
            <Option value='unit'>Unit</Option>
            <Option value='piece'>Piece</Option>
            <Option value='item'>Item</Option>
            <Option value='pack'>Pack</Option>
            <Option value='box'>Box</Option>
          </OptGroup>
        </Select>
      </ConversionBox>

      <div style={{ marginTop: 20, display: 'flex', gap: '20px' }}>
        {/* Left side - Allergen checkboxes */}
        {/* <div style={{ flex: 1 }}>
          <p>Allergens</p>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 8,
              flexDirection: 'column',
            }}
          >
            {commonAllergens.map(allergen => {
              const current = allergens[allergen] || {}
              return (
                <div
                  key={allergen}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 8,
                    padding: '8px',
                    border: '1px solid #d9d9d9',
                    borderRadius: '4px',
                    marginBottom: '8px',
                  }}
                >
                  <label style={{ minWidth: '120px', fontWeight: 'bold' }}>
                    {allergen}
                  </label>
                  <Radio.Group
                    value={current.value}
                    disabled={!canEditIngredient}
                    onChange={e =>
                      setAllergens({
                        ...allergens,
                        [allergen]: {
                          value: e.target.value,
                          edited: true,
                        },
                      })
                    }
                  >
                    <Radio value='yes'>Yes</Radio>
                    <Radio value='traces'>May Contain</Radio>
                    <Radio value='no'>No</Radio>
                  </Radio.Group>
                </div>
              )
            })}
          </div>
        </div> */}

        {/* Right side - Informational box */}
        {/* <div style={{ flex: 1, maxWidth: '300px' }}>
          <div
            style={{
              background: 'linear-gradient(135deg, #f6ffed 0%, #f0f9ff 100%)',
              border: '1px solid #b7eb8f',
              borderRadius: '8px',
              padding: '16px',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: '10px',
              }}
            >
              <div
                style={{
                  width: '28px',
                  height: '28px',
                  borderRadius: '50%',
                  background: '#52c41a',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '10px',
                  flexShrink: 0,
                }}
              >
                <span
                  style={{
                    color: 'white',
                    fontSize: '14px',
                    fontWeight: 'bold',
                  }}
                >
                  !
                </span>
              </div>
              <h4
                style={{
                  margin: 0,
                  color: '#52c41a',
                  fontSize: '14px',
                  fontWeight: '600',
                }}
              >
                Edit Product Allergens
              </h4>
            </div>
            <p
              style={{
                margin: '0 0 8px 0',
                color: '#666',
                lineHeight: '1.4',
                fontSize: '13px',
              }}
            >
              Update a product&apos;s allergens here - these flow through to
              your recipes and menus automatically. Once your menus are built,
              you&apos;ll get instant allergen grids that:
            </p>
            <ul
              style={{
                margin: '0 0 8px 0',
                paddingLeft: '16px',
                color: '#666',
                fontSize: '12px',
              }}
            >
              <li>Show allergens clearly by menu</li>
              <li>Can be downloaded as PDFs</li>
              <li>
                Or shared via QR codes on your menus and tables for patrons to
                view live
              </li>
            </ul>
            <div
              style={{
                background: '#f6ffed',
                border: '1px solid #b7eb8f',
                borderRadius: '4px',
                padding: '6px 10px',
                marginTop: '8px',
              }}
            >
              <p
                style={{
                  margin: 0,
                  color: '#52c41a',
                  fontSize: '12px',
                  fontWeight: '500',
                }}
              >
                We take care of the heavy lifting - just edit, build, and share.
                Simple.
              </p>
            </div>
          </div>
        </div> */}
      </div>
    </Modal>
  )
}

export default ConversionModal
